/**
 * Rubric Studio Home - Entry Screen
 * Three entry points: Create New, Refine Existing, Import JSON
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const RubricStudioHome: React.FC = () => {
  const navigate = useNavigate();

  const handleCreateNew = () => {
    navigate('/rubric-studio/workspace?mode=create');
  };

  const handleRefineExisting = () => {
    navigate('/rubric-studio/workspace?mode=refine');
  };

  const handleImportJSON = () => {
    navigate('/rubric-studio/workspace?mode=import');
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Home
        </MuiLink>
        <Typography color="text.primary">Rubric Studio</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Rubric Studio
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Create and refine professional rubrics with AI assistance. Choose how you want to start:
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, mt: 4, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Create New */}
        <Box sx={{ flex: 1 }}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
              <AddCircleIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Create New
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Start from scratch by uploading documents, pasting text, or describing your requirements.
                AI will help you build a comprehensive rubric.
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCreateNew}
                startIcon={<AddCircleIcon />}
              >
                Start Creating
              </Button>
            </CardActions>
          </Card>
        </Box>

        {/* Refine Existing */}
        <Box sx={{ flex: 1 }}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
              <EditIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Refine Existing
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Open one of your saved rubrics and enhance it with AI assistance, manual edits,
                or conversational refinement.
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                size="large"
                color="success"
                onClick={handleRefineExisting}
                startIcon={<EditIcon />}
              >
                Select Rubric
              </Button>
            </CardActions>
          </Card>
        </Box>

        {/* Import JSON */}
        <Box sx={{ flex: 1 }}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
              <UploadFileIcon sx={{ fontSize: 80, color: 'info.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Import JSON
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Have a rubric JSON from a previous session or external source? Paste or upload it
                to continue working on it.
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                size="large"
                color="info"
                onClick={handleImportJSON}
                startIcon={<UploadFileIcon />}
              >
                Import JSON
              </Button>
            </CardActions>
          </Card>
        </Box>
      </Box>

      {/* Help Section */}
      <Paper sx={{ mt: 6, p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          What is Rubric Studio?
        </Typography>
        <Typography variant="body2" paragraph>
          Rubric Studio is a flexible workspace for creating and refining assessment rubrics with AI assistance.
          You can work in three different views:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>Visual Preview:</strong> See your rubric as cards with inline editing
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>JSON Editor:</strong> Direct access to the raw JSON structure for power users
          </Typography>
          <Typography component="li" variant="body2">
            <strong>AI Chat:</strong> Always-available sidebar to refine your rubric conversationally
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 2 }}>
          All views stay synchronized in real-time, so you can switch between them seamlessly.
        </Typography>
      </Paper>
    </Box>
  );
};

export default RubricStudioHome;
